import { isEmpty } from "lodash";
import { RowType } from "PFApp/booking/types/row";
import React, { JSX, useEffect, useMemo, useRef } from "react";

import { CalendarMode, CalendarView } from "../../types";
import { RowDimensions, useBookingRowsContext } from "./context/booking_rows_context";
import { useBookingsOverviewContext } from "./context/bookings_overview_context";
import { SidePanelRow } from "./side_panel_row";
import { SidePanelRowTooltip } from "./side_panel_row/tooltip/side_panel_row_tooltip";
import css from "./side_panel_rows.module.scss";

interface SidePanelRowsProps {
  scrollToDate: (date: string) => void;
}

export const SidePanelRows = ({ scrollToDate }: SidePanelRowsProps): JSX.Element => {
  const {
    calendarMode,
    calendarData: { view }
  } = useBookingsOverviewContext();
  const {
    bookingsByRowKey,
    resetExpandedRowsState,
    rowsByCalendarMode,
    setRowDimensionsByCalendarMode,
    warningsByActivity,
    warningsByProfile
  } = useBookingRowsContext();
  const rows = rowsByCalendarMode[calendarMode];

  const sidePanelRowRefs = useRef<Record<string, HTMLDivElement>>({});

  //filter out Other row if no "other" bookings are available
  const displayedRows = useMemo(() => {
    if (calendarMode === CalendarMode.Projects) {
      return rows;
    }
    return rows.filter((row) => row.type !== RowType.Other || !isEmpty(bookingsByRowKey[row.key]));
  }, [rows, bookingsByRowKey]);

  useEffect(() => {
    if (!displayedRows.length) {
      return;
    }
    recalculateDimensions();
  }, [
    sidePanelRowRefs.current,
    displayedRows,
    warningsByActivity,
    warningsByProfile,
    view,
    bookingsByRowKey
  ]);

  useEffect(() => {
    if (view === CalendarView.BookingsOnly) {
      resetExpandedRowsState();
    }
  }, [view]);

  const recalculateDimensions = () => {
    setRowDimensionsByCalendarMode((prevState) => ({
      ...prevState,
      [calendarMode]: Object.entries(sidePanelRowRefs.current).reduce(
        (result, [rowKey, element]) => ({
          ...result,
          [rowKey]: {
            top: element.offsetTop,
            height: element.offsetHeight
          }
        }),
        {} as RowDimensions
      )
    }));
  };

  return (
    <div className={css.container}>
      {displayedRows.map((row) => (
        <SidePanelRowTooltip key={`${row.parents.join("_")}_${row.key}`} row={row}>
          <SidePanelRow
            row={row}
            ref={(ref) => {
              if (!ref) {
                return;
              }
              sidePanelRowRefs.current[row.key] = ref;
            }}
            scrollToDate={scrollToDate}
          />
        </SidePanelRowTooltip>
      ))}
    </div>
  );
};
